import React, { type JSX } from "react";
import { observer } from "mobx-react";
import { action, computed, makeObservable, observable } from "mobx";
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PdfViewerProps = {
  pdfData: ArrayBuffer | null;
  onLoadError?: () => void;
  onRenderPage?: (index: number, pageElem: JSX.Element) => JSX.Element | null;
};

@observer
export default class PdfViewer extends React.Component<PdfViewerProps> {
  @observable private _pagesNumber: number = 0;
  @observable private _pageWidth: number = 0;
  @observable private _loadingError: Error | null = null;
  @observable private _pdfData: ArrayBuffer | null;
  private _pdfWrapper: React.RefObject<HTMLDivElement | null> = React.createRef<HTMLDivElement>();

  constructor(props: PdfViewerProps) {
    super(props);
    this._pdfData = props.pdfData;
    makeObservable(this);
  }

  componentDidMount() {
    this._setSize();
    window.addEventListener('resize', this._onWindowResize);
  }

  componentDidUpdate(prevProps: PdfViewerProps) {
    if (prevProps.pdfData !== this.props.pdfData) {
      this._pdfData = this.props.pdfData;
      this._pagesNumber = 0;
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this._onWindowResize);
  }

  private _onWindowResize = () => {
    clearTimeout(this._resizeTimeout);
    this._resizeTimeout = setTimeout(() => this._setSize(), 500);
  };

  private _resizeTimeout?: ReturnType<typeof setTimeout>;

  private _setSize() {
    if (!this._pdfWrapper.current) return;
    const scrollbarWidthPx = 19;
    const containerPaddingPx = 3;
    const overflowWidth = scrollbarWidthPx + containerPaddingPx;
    this._pageWidth = this._pdfWrapper.current.getBoundingClientRect().width - overflowWidth;
  }

  @computed
  private get source() {
    return this._pdfData ? { data: this._pdfData } : null;
  }

  render() {
    return (
      <div className="pdf-container" ref={this._pdfWrapper}>
        <style>{`
          .pdf-container {
            overflow: auto;
            max-height: 70vh;
            background: var(--bg-secondary);
            border: 1px solid var(--border-color);
            border-radius: 4px;
            padding: 16px;
          }

          .pdf-container .react-pdf__Document {
            display: flex;
            flex-direction: column;
            gap: 16px;
          }

          .pdf-container .react-pdf__Page {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            border-radius: 4px;
            overflow: hidden;
          }

          .pdf-container .react-pdf__Page canvas {
            max-width: 100%;
            height: auto !important;
          }

          .pdf-loading {
            padding: 20px;
            text-align: center;
            color: var(--text-secondary);
          }

          .pdf-error {
            padding: 20px;
            text-align: center;
            color: var(--danger-color);
          }
        `}</style>

        <Document
          file={this.source}
          noData={this.renderNoData()}
          loading={this.renderNoData()}
          onLoadSuccess={this._onLoadSuccess}
          onSourceError={this._onLoadError}
          onLoadError={this._onLoadError}
        >
          {Array.from(
            new Array(this._pagesNumber),
            (_, index) => this.renderPage(index),
          )}
        </Document>
        {this._loadingError && <div className="pdf-error">{this._loadingError.message}</div>}
      </div>
    );
  }

  renderPage = (index: number) => {
    const { onRenderPage } = this.props;
    const page = (
      <Page
        key={`page_${index + 1}`}
        pageNumber={index + 1}
        width={this._pageWidth || 400}
      />
    );

    if (onRenderPage) {
      return onRenderPage(index, page);
    }

    return page;
  };

  renderNoData() {
    return (
      <div className="pdf-loading">
        Loading PDF preview...
      </div>
    );
  }

  @action.bound
  private async _onLoadSuccess(doc: { numPages: number }) {
    this._pagesNumber = doc.numPages;
    this._loadingError = null;
  }

  @action.bound
  private async _onLoadError(error: Error) {
    this._pagesNumber = 0;
    this._loadingError = error;
    this.props.onLoadError?.();
  }
}
